
import { toast } from 'react-hot-toast';
import { CampaignFormData, WorkflowStep } from '../../types';
import { 
  findOrCreateCampaign, 
  getCampaignDataSimple 
} from '../../utils/firebaseSimple';
import { sanitizeForSerialization } from '../../utils/dataSerializer';

const INITIAL_STEPS: WorkflowStep[] = [
  // STAGE 1: RESEARCH & INTELLIGENCE
  {
    id: 1,
    title: 'Political Context Research',
    description: 'Deep investigative analysis of political narratives, stakeholders, and implicit influences',
    status: 'pending',
    template: 'playingToWinTemplates.politicalContextResearch',
    result: '',
    stage: 'research'
  },
  {
    id: 2,
    title: 'Political Context Refinement',
    description: 'In-depth analysis of industry influence, ideological biases, and historical patterns',
    status: 'pending',
    template: 'playingToWinTemplates.politicalContextRefinement',
    result: '',
    stage: 'research',
    dependencies: [1]
  },
  
  // STAGE 2: WINNING ASPIRATION
  {
    id: 3,
    title: 'Winning Aspiration',
    description: 'Define victory and establish success metrics with research insights',
    status: 'pending',
    template: 'playingToWinTemplates.winningAspiration',
    result: '',
    stage: 'aspiration',
    dependencies: [1, 2]
  },
  
  // STAGE 3: WHERE TO PLAY - EXPANDED
  {
    id: 4,
    title: 'Decision Makers Analysis',
    description: 'Enhanced decision maker mapping with influence networks and access strategies',
    status: 'pending',
    template: 'playingToWinTemplates.whereToPlayDecisionMakers',
    result: '',
    stage: 'where-to-play',
    dependencies: [3]
  },
  {
    id: 5,
    title: 'Constituencies & Stakeholders',
    description: 'Deep stakeholder analysis with power mapping and coalition opportunities',
    status: 'pending',
    template: 'playingToWinTemplates.whereToPlayStakeholders',
    result: '',
    stage: 'where-to-play',
    dependencies: [4]
  },
  {
    id: 6,
    title: 'Message Frame Analysis',
    description: 'Comprehensive linguistic and cognitive frame analysis using academic frameworks',
    status: 'pending',
    template: 'playingToWinTemplates.messageFrameAnalysis',
    result: '',
    stage: 'where-to-play',
    dependencies: [4, 5]
  },
  {
    id: 7,
    title: 'Opposition Research & Counter-Strategy',
    description: 'Systematic opposition analysis and counter-narrative development',
    status: 'pending',
    template: 'playingToWinTemplates.oppositionResearch',
    result: '',
    stage: 'where-to-play',
    dependencies: [5, 6]
  },
  {
    id: 8,
    title: 'Channels & Platforms Strategy',
    description: 'Communication strategy with audience-channel alignment and reach optimization',
    status: 'pending',
    template: 'playingToWinTemplates.whereToPlayChannels',
    result: '',
    stage: 'where-to-play',
    dependencies: [6, 7]
  },
  
  // STAGE 4: HOW TO WIN
  {
    id: 9,
    title: 'Theory of Change',
    description: 'Comprehensive strategy synthesis with multiple pathways and contingencies',
    status: 'pending',
    template: 'playingToWinTemplates.howToWinTheory',
    result: '',
    stage: 'how-to-win',
    dependencies: [4, 5, 6, 7, 8]
  },
  
  // STAGE 5: MESSAGING GUIDE
  {
    id: 10,
    title: 'Message Development Guide',
    description: 'Detailed messaging guide with values mapping, targeted messages, and implementation',
    status: 'pending',
    template: 'playingToWinTemplates.messageDevelopment',
    result: '',
    stage: 'messaging',
    dependencies: [6, 7, 9]
  },
  
  // STAGE 6: IMPLEMENTATION
  {
    id: 11,
    title: 'Core Capabilities Assessment',
    description: 'Assess required capabilities and identify capacity gaps for campaign execution',
    status: 'pending',
    template: 'playingToWinTemplates.coreCapabilities',
    result: '',
    stage: 'capabilities',
    dependencies: [9, 10]
  },
  {
    id: 12,
    title: 'Campaign Plan & Management Systems',
    description: 'Comprehensive campaign plan with management systems and operational framework',
    status: 'pending',
    template: 'playingToWinTemplates.campaignPlan',
    result: '',
    stage: 'management',
    dependencies: [9, 10, 11]
  }
];

interface InitializeCampaignParams {
  userId: string;
  formData: CampaignFormData;
  initializationAttempts: number;
  setInitializationAttempts: (fn: (prev: number) => number) => void;
  setCampaignId: (id: string) => void;
  setInitializationError: (error: string | null) => void;
  workflowAnalysis: any;
  setShouldStartAnalysis: (should: boolean) => void;
  setShowSummary: (show: boolean) => void;
  setConsolidatedReport: (report: string | null) => void;
  setIsInitializing: (initializing: boolean) => void;
}

export const initializeCampaign = async ({
  userId,
  formData,
  initializationAttempts,
  setInitializationAttempts,
  setCampaignId,
  setInitializationError,
  workflowAnalysis,
  setShouldStartAnalysis,
  setShowSummary,
  setConsolidatedReport,
  setIsInitializing
}: InitializeCampaignParams) => {
  if (initializationAttempts >= 3) {
    console.error('Max initialization attempts reached');
    setInitializationError('Failed to initialize campaign after multiple attempts. Please refresh the page.');
    setIsInitializing(false);
    return;
  }

  try {
    setIsInitializing(true);
    setInitializationAttempts(prev => prev + 1);
    console.log(`🚀 Starting campaign initialization attempt ${initializationAttempts + 1}`);
    
    // Add timeout for initialization
    const initTimeout = setTimeout(() => {
      console.error('⏰ Campaign initialization timed out');
      setInitializationError('Initialization timed out. Please try again.');
      setIsInitializing(false);
    }, 30000);
    
    // Sanitize form data before using it
    const sanitizedFormData = sanitizeForSerialization(formData);
    console.log('🧹 Form data sanitized for Firebase operations');
    
    // Use the new findOrCreateCampaign function instead of always creating new
    const id = await findOrCreateCampaign(userId, sanitizedFormData);
    console.log('✅ Campaign found/created with ID:', id);
    setCampaignId(id);
    clearTimeout(initTimeout);
    setInitializationError(null);

    // Load existing analysis state
    const existingData = await getCampaignDataSimple(id);
    
    if (existingData && typeof existingData === 'object' && 'analysisState' in existingData) {
      const analysisState = existingData.analysisState as any;
      const { steps: savedSteps, currentStep: savedStep, isComplete: completed } = analysisState;
      
      if (savedSteps && Array.isArray(savedSteps) && savedSteps.length > 0) {
        const finalSteps = Array.isArray(savedSteps) ? savedSteps : INITIAL_STEPS;
        const sanitizedCurrentStep = Number(savedStep) || 1;
        const sanitizedCompleted = Boolean(completed);
        
        console.log('Restoring workflow state with steps:', finalSteps);
        
        workflowAnalysis.setSteps(finalSteps);
        workflowAnalysis.setCurrentStep(sanitizedCurrentStep);
        workflowAnalysis.setIsComplete(sanitizedCompleted);
        setShouldStartAnalysis(!sanitizedCompleted);
        
        if (sanitizedCompleted) {
          setShowSummary(true);
          if (analysisState.consolidatedReport) {
            setConsolidatedReport(analysisState.consolidatedReport);
          }
        }
      } else {
        console.log('No valid saved steps found, using initial steps');
        setShouldStartAnalysis(true);
      }
    } else {
      console.log('No existing analysis state found, starting fresh');
      setShouldStartAnalysis(true);
    }
    
    setIsInitializing(false);
    console.log('Campaign initialization completed successfully');
    
  } catch (error) {
    console.error('Error initializing campaign:', error);
    setIsInitializing(false);
    
    if (error instanceof Error) {
      setInitializationError(`Initialization error (attempt ${initializationAttempts + 1}): ${error.message}`);
      toast.error(`Error initializing campaign: ${error.message}`);
    } else {
      setInitializationError('Unknown error occurred during initialization.');
      toast.error('Unknown error occurred. Please refresh and try again.');
    }
  }
};

export { INITIAL_STEPS };
